"use client";

import { Send } from "lucide-react";
import { useRouter } from "next/navigation";
import { useState, useTransition } from "react";
import { toast } from "sonner";
import { createReviewResponse } from "@/actions/review";
import { MentionTextarea } from "@/components/mentions/mention-textarea";
import { Button } from "@/components/ui/button";
import type { ReviewResponseType } from "@/db/schema/review";
import { cn } from "@/lib/utils";

type ReviewResponseFormProps = {
  publicId: string;
  responseType: ReviewResponseType;
  initialBody?: string;
  buttonLabel: string;
  placeholder: string;
  className?: string;
  templates?: string[];
  onSubmitted?: () => void;
};

const responseLimits = {
  owner_reply: 2000,
  reviewer_follow_up: 1500,
  comment: 1000,
} satisfies Record<ReviewResponseType, number>;

export function ReviewResponseForm({
  publicId,
  responseType,
  initialBody = "",
  buttonLabel,
  placeholder,
  className,
  templates = [],
  onSubmitted,
}: ReviewResponseFormProps) {
  const router = useRouter();
  const [body, setBody] = useState(initialBody);
  const [isPending, startTransition] = useTransition();
  const maxLength = responseLimits[responseType];

  const handleSubmit = () => {
    const trimmed = body.trim();
    if (!trimmed) {
      toast.error("Add a reply before posting");
      return;
    }
    if (trimmed.length > maxLength) {
      toast.error(`Keep this under ${maxLength} characters`);
      return;
    }

    startTransition(async () => {
      const result = await createReviewResponse({
        publicId,
        responseType,
        body: trimmed,
      });

      if (!result.ok) {
        toast.error(result.message);
        return;
      }

      if (responseType === "comment") setBody("");
      toast.success(
        responseType === "owner_reply" ? "Reply saved" : "Comment posted",
      );
      onSubmitted?.();
      router.refresh();
    });
  };

  return (
    <div className={cn("space-y-2", className)}>
      {templates.length > 0 ? (
        <div className="flex flex-wrap gap-2">
          {templates.map((template) => (
            <button
              key={template}
              type="button"
              onClick={() => setBody(template)}
              className="hover:bg-surface-hover rounded-full border border-border bg-surface-alt px-3 py-1.5 text-xs font-medium text-text-secondary transition active:scale-95 dark:border-glass-border-10 dark:bg-glass-bg-05 dark:text-white/70"
            >
              {template.slice(0, 42)}
              {template.length > 42 ? "..." : ""}
            </button>
          ))}
        </div>
      ) : null}
      <MentionTextarea
        value={body}
        onChange={setBody}
        placeholder={placeholder}
        disabled={isPending}
        maxLength={maxLength}
        className="min-h-24 rounded-xl text-sm"
      />
      <div className="flex items-center justify-between gap-3">
        <span className="text-xs text-text-tertiary tabular-nums">
          {body.trim().length}/{maxLength}
        </span>
        <Button
          type="button"
          size="sm"
          className="rounded-full active:scale-95"
          disabled={isPending || body.trim().length === 0}
          onClick={handleSubmit}
        >
          <Send className="h-4 w-4" />
          {buttonLabel}
        </Button>
      </div>
    </div>
  );
}
